import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  ArrowRight, Badge as BadgeIcon, Bell, Check, ChevronDown, ChevronRight, Crown,
  Download, Film, Home as HomeIcon, Info, LayoutTemplate, ListMusic, Loader2,
  Music2, Pause, Play, Plus, RefreshCw, Settings, UsersRound, X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { API, api } from "@/lib/api";
import AppCreateWizard from "@/components/app/AppCreateWizard";
import VideoCreate from "@/components/app/VideoCreate";
import FaqPage from "@/components/app/FaqPage";
import InfoGlassBadge from "@/components/app/InfoGlassBadge";
import OccasionCard from "@/components/app/OccasionCard";
import PremiumCard from "@/components/app/PremiumCard";
import { EVENTS, GENRE_SHORT, HOME_EVENTS, PRICE_PER_SONG, fmtDate, fmtDuration, getClientId, getTgUser, listenUrlOf, songWord, statusMeta, trackApp, trackAppEnter } from "@/components/app/appData";
import "./apphome.css";
import "./card-colors.css";

const A = "/design/";

/* Мини-апп всегда открывается в мобильном виде — в том числе на ПК
   (заказчик: на десктопе должна быть мобильная версия, не полная). */

function MusicMark() {
  return <span className="music-mark" aria-hidden="true"><i /><i /><i /><i /><i /></span>;
}

function Brand() {
  return <div className="brand"><MusicMark /><span className="brand-name">E-PISNYA</span></div>;
}

function FaqTopButton({ active = false, onClick, size = 36 }) {
  return <button type="button"
    className={"icon-btn faq-top-btn" + (active ? " active" : "")}
    style={{ height: size }}
    onClick={onClick} aria-label="Часті питання">
    <span className="faq-top-text">INFO</span>
  </button>;
}

const NAV = [
  { id: "home", icon: HomeIcon, label: "Головна" },
  { id: "create", icon: Music2, label: "Створити пісню" },
  { id: "songs", icon: ListMusic, label: "Мої пісні" },
  { id: "templates", icon: LayoutTemplate, label: "Шаблони" },
  { id: "pricing", icon: BadgeIcon, label: "Тарифи" },
  { id: "partner", icon: UsersRound, label: "Партнерка" },
  { id: "settings", icon: Settings, label: "Налаштування" },
];

const MOBILE_NAV = [
  { id: "home", icon: "home", label: "Головна" },
  { id: "create", icon: "music", label: "Створити" },
  { id: "songs", icon: "like", label: "Мої пісні" },
  { id: "pricing", icon: "star", label: "Тарифи" },
];

function VideosList({ items, loading, onInfo }) {
  if (loading) {
    return (
      <div className="vid-list-empty">
        <span className="stub-loading"><Loader2 size={15} className="spin" /> Завантажую…</span>
      </div>
    );
  }
  if (!(items || []).length) {
    return (
      <div className="vid-list-empty">
        Відео ще немає. Створіть перше на сторінці «Музика на фото».
      </div>
    );
  }
  return (
    <div className="vid-list">
      {(items || []).map((v) => (
        <div className="vid-list-row glass-surface" key={v.id} data-testid="video-row">
          {v.thumb
            ? <img className="vid-list-thumb" src={v.thumb} alt="" />
            : <span className="vid-preview sm"><Film size={18} /></span>}
          <span className="vid-song-info">
            <b>{v.song_title || "Відео"}</b>
            <i>
              {v.photos} фото · {v.format}
              {(v.status === "queued" || v.status === "processing") ? " · створюється…" : ""}
              {v.status === "awaiting" ? " · чек на перевірці" : ""}
              {v.status === "rejected" ? " · оплата відхилена" : ""}
              {v.status === "failed" ? " · помилка" : ""}
            </i>
          </span>
          {v.status === "done" && (
            <button type="button" className="song-info" aria-label="Деталі"
              onClick={(e) => { e.stopPropagation(); onInfo?.(v); }} data-testid="video-info">
              <img src="/design/icons-lg/info.svg" alt="" />
            </button>
          )}
          {(v.status === "queued" || v.status === "processing") && (
            <span className="vid-list-spin"><Loader2 size={16} /></span>
          )}
        </div>
      ))}
    </div>
  );
}

function IconLG({ name, size = 22 }) {
  return <img className="iconlg" src={A + "icons-lg/" + name + ".svg"} alt="" style={{ width: size, height: size }} />;
}

function Sidebar({ section, onSection, onOpenCreate, createActive, onPremium }) {
  return <aside className="sidebar">
    <Brand />
    <nav className="nav">
      {NAV.map((n) => {
        const Ico = n.icon;
        const active = n.id === "create" ? createActive : (!createActive && section === n.id);
        return <button key={n.id} className={"nav-item" + (active ? " active" : "")}
          onClick={() => (n.id === "create" ? onOpenCreate("") : onSection(n.id))}>
          <Ico size={19} strokeWidth={1.9} /><span>{n.label}</span>
        </button>;
      })}
    </nav>
    <div className="premium-card">
      <div className="premium-head"><Crown size={16} strokeWidth={2} /> Преміум</div>
      <p className="premium-list">Більше варіантів<br />Краща якість<br />Швидша генерація</p>
      <button className="premium-btn" onClick={onPremium}>Оновити</button>
    </div>
  </aside>;
}

function Topbar({ onFaq, faqActive, onPlus, onBell, bellOpen, me, balOpen, onBal, onTopup }) {
  return <header className={"topbar" + ((bellOpen || balOpen) ? " pop-open" : "")}>
    <div className="top-actions">
      <BalanceChip me={me} open={balOpen} onToggle={onBal} />
      <button className="icon-btn plus-btn" onClick={onPlus} aria-label="Поповнити баланс"><Plus size={19} strokeWidth={2.2} /></button>
      <button className={"icon-btn notification-circle" + (bellOpen ? " active" : "")} onClick={onBell} aria-label="Сповіщення"><Bell size={18} strokeWidth={1.9} /></button>
      <FaqTopButton active={faqActive} onClick={onFaq} size={40} />
      <Notifications open={bellOpen} onClose={() => onBell()} />
      <BalancePop open={balOpen} me={me} onClose={() => onBal()} onTopup={onTopup} />
    </div>
  </header>;
}

function Notifications({ open, onClose, draft, onResume, onDismiss }) {
  if (!open) return null;
  return <>
    <div className="pop-backdrop" onClick={onClose} />
    <div className="notif-pop glass-surface">
      <div className="notif-head"><Bell size={15} /> Сповіщення<span className="notif-count">{draft ? 1 : 0}</span></div>
      {draft ? (
        <button type="button" className="notif-task" data-testid="notif-resume"
          onClick={() => { onDismiss?.(); onResume?.(draft); }}>
          <span className="notif-task-t">Ви почали, але не завершили</span>
          <span className="notif-task-d">Ваша пісня чекає — продовжіть з того ж місця.</span>
          <span className="notif-task-link">Продовжити <ArrowRight size={14} /></span>
        </button>
      ) : (
        <p className="notif-text">Нових сповіщень немає. Тут з'являться статуси ваших пісень.</p>
      )}
    </div>
  </>;
}

function BalanceChip({ me, open, onToggle }) {
  const n = me ? Number(me.balance) || 0 : null;
  return <button type="button" className={"balance-chip" + (open ? " active" : "")}
    data-testid="balance-chip" onClick={onToggle} aria-label="Баланс генерацій">
    <img className="iconlg" src={A + "icons-lg/music.svg"} alt="" style={{ width: 15, height: 15 }} />
    <b>{n === null ? "—" : n}</b>
  </button>;
}

function BalancePop({ open, me, onClose, onTopup }) {
  if (!open) return null;
  const n = me ? Number(me.balance) || 0 : 0;
  return <>
    <div className="pop-backdrop" onClick={onClose} />
    <div className="balance-pop glass-surface" data-testid="balance-pop">
      <div className="bp-head">Баланс генерацій</div>
      <div className="bp-count">{n}<span>{songWord(n)}</span></div>
      <p className="bp-note">1 генерація = 1 пісня (2 варіанти).<br />Куплені генерації не згорають.</p>
      <button className="bp-topup" onClick={onTopup}><Plus size={15} strokeWidth={2.6} /> Поповнити</button>
      <span className="bp-ver">v8</span>
    </div>
  </>;
}

function Hero({ mobile = false, onCta }) {
  return <div className={"hero " + (mobile ? "hero-mobile" : "hero-desktop")}>
    <div className="hero-media" aria-hidden="true">
      <img src={A + (mobile ? "banners/blue-mobile.webp" : "banners/blue-desktop.webp")} alt="" />
    </div>
    <div className="hero-body">
      <h1 className="hero-title">Твоя історія<br />звучить в пісні</h1>
      <p className="hero-desc">Для будь-якої події,<br />та за кілька хвилин</p>
      <button className="hero-cta" onClick={onCta}><Music2 size={19} strokeWidth={2.2} /> Створити пісню</button>
    </div>
  </div>;
}

function SectionTitle({ children, action, onAction }) {
  return <div className="sec-head">
    <h2>{children}</h2>
    {action && <button className="sec-link" onClick={onAction}>{action} <ArrowRight size={14} /></button>}
  </div>;
}

function EventCards({ onPick, grid = false }) {
  const list = grid
    ? HOME_EVENTS.map((key) => EVENTS.find((e) => e.key === key)).filter(Boolean)
    : EVENTS;
  return <div className={"occ-grid" + (grid ? " occ-grid-home" : "")}>
    {list.map((e, i) => (
      <OccasionCard key={e.key} eventKey={e.key} label={e.label} arrow={!grid} onPick={onPick} delay={i * 30} />
    ))}
  </div>;
}

function Waveform({ seed = 1, className = "" }) {
  const gid = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const rnd = (n) => {
    const x = Math.sin(n) * 10000;
    return x - Math.floor(x);
  };
  const bars = Array.from({ length: 54 }, (_, i) => {
    const envelope = 0.62 + 0.38 * Math.sin(((i + 1) / 55) * Math.PI);
    return 7 + rnd(i * 1.7 + seed * 13.31) * 24 * envelope;
  });
  return <svg className={"song-wave " + className} viewBox="0 0 220 34" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#29B6FF" />
        <stop offset="1" stopColor="#2F80FF" />
      </linearGradient>
    </defs>
    {bars.map((h, i) => (
      <rect key={i} x={i * 4.05 + 1.2} y={(34 - h) / 2} width="2.3" height={h} rx="1.15"
        fill={`url(#${gid})`} opacity={0.9} />
    ))}
  </svg>;
}

const isDone = (it) => it?.status === "done" || it?.status === "completed";

// Готові пісні — всегда сверху (по дате), остальные — ниже по времени создания.
const sortSongs = (items) => [...items].sort((a, b) => {
  const da = isDone(a) ? 1 : 0;
  const db = isDone(b) ? 1 : 0;
  if (da !== db) return db - da;
  return String(b.created_at || "").localeCompare(String(a.created_at || ""));
});

const MOOD_LABELS = { romantic: "Романтичний", fun: "Веселий", touching: "Зворушливий", neutral: "Нейтральний" };
const LANG_LABELS = { uk: "Українська", ru: "Русский", en: "English" };

function SongDetailsModal({ item, onClose }) {
  const [descOpen, setDescOpen] = useState(false);
  if (!item) return null;
  const st = statusMeta(item);
  const ev = EVENTS.find((e) => e.key === item.event);
  const url = listenUrlOf(item);
  const rows = [
    ["Статус", st.label],
    ["Привід", ev ? ev.label + (item.event_text ? ` — ${item.event_text}` : "") : (item.event_text || "")],
    ["Жанр", (item.genre_mix || []).length
      ? `Мікс: ${item.genre_mix_label || item.genre_mix.join(" + ")}`
      : (item.genre_label || GENRE_SHORT[item.genre] || item.genre)],
    ["Настрій", item.mood_text || MOOD_LABELS[item.mood] || ""],
    ["Мова", LANG_LABELS[item.song_language] || ""],
    ["Голос", item.voice_label || ""],
    ["Номер", item.number ? `#${item.number}` : ""],
    ["Дата", fmtDate(item.created_at)],
  ].filter((r) => r[1]);
  return createPortal(
    <div className="epw-modal-wrap sd-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal sd-modal" data-testid="song-details">
        <div className="epw-modal-head">
          <div className="epw-modal-title">{(item.genre_mix || []).length ? "Мікс" : (item.genre_label || "Деталі замовлення")}</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        <div className="sd-body">
        <div className="sd-rows">
          {rows.map(([k, v]) => (
            <div className="sd-row" key={k}>
              <span className="sd-k">{k}</span>
              <span className="sd-v" style={k === "Статус" ? { color: st.color, fontWeight: 700 } : undefined}>{v}</span>
            </div>
          ))}
        </div>
        {item.description ? (
          <div className="sd-desc">
            <div className="sd-k">Опис</div>
            <p className={descOpen ? "open" : ""}>{item.description}</p>
            {item.description.length > 220 && (
              <button type="button" className="sd-more" data-testid="sd-more"
                onClick={() => setDescOpen((v) => !v)}>
                {descOpen ? "Згорнути" : "Показати повністю"}
              </button>
            )}
          </div>
        ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function TrackRow({ track }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };
  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const t = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * dur;
    el.currentTime = t;
    setCur(t);
  };
  const fmtT = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const pct = dur ? (cur / dur) * 100 : 0;
  return (
    <div className="st-track">
      <audio ref={audioRef} src={track.url} preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
        onEnded={() => { setPlaying(false); setCur(0); }} />
      <button type="button" className="st-play" onClick={toggle} aria-label={playing ? "Пауза" : "Грати"}>
        {playing ? <Pause size={17} color="#fff" /> : <Play size={17} color="#fff" fill="#fff" />}
      </button>
      <div className="st-main">
        <span className="st-n">{track.title || `Варіант ${(track.idx ?? 0) + 1}`}</span>
        <div className="st-bar" onClick={seek}><i style={{ width: `${pct}%` }} /></div>
        <div className="st-time"><span>{fmtT(cur)}</span><span>{fmtT(dur)}</span></div>
      </div>
    </div>
  );
}

const EX_GENRE_COLOR = {
  guitar: "#38BDF8", pop: "#FACC15", rnb: "#EC4899", dance: "#F43F5E",
  zastilna: "#6366F1", lightrock: "#8B5CF6", rock: "#8B5CF6", rap: "#10B981",
  deephouse: "#F43F5E", disney: "#EC4899", custom: "#38A8FF",
};
const EX_MOOD_STYLE = {
  touching: { bg: "rgba(32,139,255,.12)", bd: "#246BB1", tx: "#91D0FF" },
  fun: { bg: "rgba(255,196,37,.12)", bd: "#9B791A", tx: "#FFD95C" },
  romantic: { bg: "rgba(236,72,153,.12)", bd: "#A82D78", tx: "#FF82C7" },
  neutral: { bg: "rgba(145,172,208,.08)", bd: "#526985", tx: "#B8C8DF" },
};
const WIZ_SOUND = ["guitar", "pop", "rnb", "dance", "zastilna", "lightrock", "rock", "rap", "deephouse", "disney"];
const WIZ_MOOD = ["romantic", "fun", "touching", "neutral"];

function exBars(seedStr, n = 46) {
  let seed = 0;
  const str = String(seedStr || "ex");
  for (let i = 0; i < str.length; i += 1) seed = (seed * 131 + str.charCodeAt(i)) >>> 0;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  return Array.from({ length: n }, (_, i) => {
    const env = 0.55 + 0.45 * Math.sin(((i + 1) / (n + 1)) * Math.PI);
    return Math.round(4 + rnd() * 26 * env);
  });
}

function ExampleCard({ item, active, onActivate, onCreateStyle }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(Number(item.duration) || 0);
  const [menuOpen, setMenuOpen] = useState(false);
  const waveRef = useRef(null);
  const [waveW, setWaveW] = useState(0);
  const exId = item.ex_id;
  useEffect(() => {
    const el = waveRef.current;
    if (!el) return undefined;
    const upd = () => setWaveW(el.clientWidth || 0);
    upd();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(upd);
      ro.observe(el);
    }
    return () => { if (ro) ro.disconnect(); };
  }, []);
  useEffect(() => {
    const el = audioRef.current;
    if (el && !active && !el.paused) el.pause();
  }, [active]);
  useEffect(() => {
    const el = audioRef.current;
    return () => { if (el) el.pause(); };
  }, []);
  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };
  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const t = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * dur;
    el.currentTime = t;
    setCur(t);
  };
  const fmtT = (s) => (s > 0 ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");
  const pct = dur ? cur / dur : 0;
  const genres = ((item.genres && item.genres.length ? item.genres : [item.genre]) || []).filter(Boolean);
  const labels = (item.genre_labels && item.genre_labels.length ? item.genre_labels : [item.genre_label]).filter(Boolean);
  const colors = item.color ? [item.color] : (genres.length ? genres.map((g) => EX_GENRE_COLOR[g] || "#38A8FF") : ["#38A8FF"]);
  const genreStr = labels.join(" + ");
  const nameRaw = String(item.title || "").trim();
  const nameShown = item.show_title && nameRaw
    ? (Array.from(nameRaw).length > 10
      ? Array.from(nameRaw).slice(0, 10).join("") + "…" : nameRaw)
    : "";
  const title = [genreStr, nameShown].filter(Boolean).join(" · ") || nameRaw || "Приклад";
  const titleFull = [genreStr, nameRaw].filter(Boolean).join(" · ") || "Приклад";
  const mood = EX_MOOD_STYLE[item.mood] || EX_MOOD_STYLE.neutral;
  const barCount = Math.max(12, Math.min(110, Math.floor((waveW + 3) / 7)));
  const bars = useMemo(() => exBars(exId || item.title, barCount), [exId, item.title, barCount]);
  const barColor = (i) => colors[Math.min(colors.length - 1, Math.floor((i / bars.length) * colors.length))];
  const goCreateStyle = () => {
    setMenuOpen(false);
    const gs = genres.filter((g) => WIZ_SOUND.includes(g));
    const draft = {};
    if (gs.length >= 2) { draft.genre = "mix"; draft.genre_mix = gs.slice(0, 2); }
    else if (gs.length === 1) draft.genre = gs[0];
    if (WIZ_MOOD.includes(item.mood)) draft.mood = item.mood;
    onCreateStyle?.(draft);
  };
  return (
    <div className={"ex-card" + (playing ? " is-live" : "")} data-testid={`example-${exId}`}>
      <audio ref={audioRef} src={item.url} preload="metadata"
        onPlay={() => {
          setPlaying(true);
          onActivate?.(exId);
          const t = item.title || titleFull;
          trackApp(`приклади: прослуховування «${t}»`,
            [labels.join(" + "), item.mood_label].filter(Boolean).join(" · "));
        }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onLoadedMetadata={(e) => { if (!dur) setDur(e.target.duration || 0); }}
        onEnded={() => { setPlaying(false); setCur(0); }} />
      <button type="button" className="ex-play" onClick={toggle}
        data-testid={`ex-play-${exId}`} aria-label={playing ? "Пауза" : "Слухати"}>
        {playing ? <Pause size={18} color="#fff" fill="#fff" /> : <Play size={18} color="#fff" fill="#fff" />}
      </button>
      <div className="ex-mid">
        <div className="ex-title-row">
          <span className="ex-title" title={titleFull}>{title}</span>
          {playing && dur > 0 ? <span className="ex-dur">{fmtT(dur)}</span> : null}
        </div>
        {item.mood_label ? (
          <span className="ex-mood" style={{ background: mood.bg, borderColor: mood.bd, color: mood.tx }}>
            {item.mood_label}
          </span>
        ) : null}
      </div>
      <div className="ex-wave-wrap" ref={waveRef} onClick={seek} data-testid={`ex-wave-${exId}`}>
        {bars.map((h, i) => (
          <span key={i} className="ex-bar"
            style={{
              height: `${h}px`,
              background: barColor(i),
              opacity: cur > 0 ? (i / bars.length <= pct ? 1 : 0.35) : 0.55,
            }} />
        ))}
      </div>
      <div className="ex-menu-wrap">
        <button type="button" className="ex-arrow" onClick={() => setMenuOpen((v) => !v)}
          aria-label="Меню" data-testid={`ex-arrow-${exId}`}>
          <ChevronRight size={20} />
        </button>
        {menuOpen && (
          <>
            <div className="ex-menu-back" onClick={() => setMenuOpen(false)} />
            <div className="ex-menu">
              <button type="button" data-testid={`ex-create-${exId}`} onClick={goCreateStyle}>
                <Music2 size={14} /> Створити пісню в цьому стилі
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ExamplesBanner({ onOpen }) {
  return <section className="ex-home-sec" data-testid="examples-home">
    <SectionTitle>Приклади звучання</SectionTitle>
    <button type="button" className="vid-home-card" data-testid="ex-open-btn" onClick={onOpen}>
      <span className="vid-home-visual">
        <img src="/design/ex-home.webp" alt="" />
      </span>
      <span className="vid-home-text">
        <b>Прослухай різні жанри</b>
        <i>Щоб знати як може звучати твоя пісня</i>
      </span>
      <ChevronRight size={20} className="vid-home-arrow" />
    </button>
  </section>;
}

function ExamplesPage({ items, onCreate }) {
  const [activeId, setActiveId] = useState(null);
  return <div data-testid="examples-page">
    <div className="ex-list">
      {items.map((it) => (
        <ExampleCard key={it.ex_id} item={it} active={activeId === it.ex_id}
          onActivate={setActiveId} onCreateStyle={onCreate} />
      ))}
    </div>
    <button type="button" className="hero-cta ex-create-btn" data-testid="ex-create-btn" onClick={() => onCreate()}>
      <Music2 size={18} strokeWidth={2.2} /> Створити пісню
    </button>
  </div>;
}

function SongTracksModal({ item, onClose }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [left, setLeft] = useState(0);
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!left) return undefined;
    const id = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [left]);
  useEffect(() => {
    if (!item) return undefined;
    const tgId = getTgUser()?.id;
    if (!tgId) return undefined;
    let alive = true;
    api.get("/public/app/send-tracks/status", { params: { tg_id: tgId } })
      .then((r) => { if (alive && r.data?.next_in > 0) setLeft(r.data.next_in); })
      .catch(() => {});
    return () => { alive = false; };
  }, [item]);
  if (!item) return null;
  const code = (listenUrlOf(item).match(/\/s\/([A-Za-z0-9]+)/) || [])[1] || "";
  const tracks = (item.tracks || []).map((t, i) => (
    typeof t === "string"
      ? { url: t, idx: i, title: item.song_title || "" }
      : { ...t, idx: t.idx ?? i, title: t.title || item.song_title || "" }
  ));
  const send = async () => {
    if (busy || left > 0 || !code) return;
    const tgId = getTgUser()?.id;
    if (!tgId) { setStatus("Відкрийте через Telegram, щоб отримати пісні в боті."); return; }
    setBusy(true); setStatus("");
    try {
      await api.post("/public/app/send-tracks", { code, tg_id: tgId });
      setSent(true);
      setLeft(600);
    } catch (e) {
      const d = String(e?.response?.data?.detail || "");
      const m = d.match(/rate-limit:(\d+)/);
      if (m) { setLeft(parseInt(m[1], 10)); setStatus("Ліміт: раз на 10 хвилин."); }
      else setStatus("Не вдалося надіслати. Спробуйте пізніше.");
    } finally { setBusy(false); }
  };
  return createPortal(
    <div className="epw-modal-wrap" role="dialog" aria-modal="true">
      <div className="epw-modal-back" onClick={onClose} />
      <div className="epw-modal st-modal" data-testid="song-tracks">
        <div className="epw-modal-head">
          <div className="epw-modal-title">{item.song_title || "Ваша пісня"}</div>
          <button type="button" className="epw-iconbtn" onClick={onClose} aria-label="Закрити"><X size={18} /></button>
        </div>
        {item.number ? <p className="st-num">Замовлення #{item.number}</p> : null}
        <div className="st-tracks">
          {tracks.map((t) => <TrackRow key={t.idx} track={t} />)}
          {tracks.length === 0 && <p className="st-status">Треки ще недоступні.</p>}
        </div>
        {code ? (
          <>
            <div className="st-divider" />
            <p className="st-info-text">
              Для завантаження або надсилання пісень у Viber / Telegram / WhatsApp тощо — натисніть
              кнопку нижче: вона надішле ваші пісні та посилання прямо в чат бота.
            </p>
            <button type="button" className="st-send" onClick={send} disabled={busy || left > 0}>
              {busy ? <Loader2 size={16} className="spin" /> : null}
              {left > 0
                ? `Можна через ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`
                : (sent ? "Надіслано в бот" : "Надіслати в бот")}
            </button>
            {status && <p className="st-status">{status}</p>}
          </>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

function SongRow({ item, onOpen, onResume, onDetails }) {
  const url = listenUrlOf(item);
  const st = statusMeta(item);
  const resume = !url && item.status === "unpaid";
  const cover = item.event ? `${A}events3d/${item.event}.webp` : `${A}track-love.jpg`;
  const badge = GENRE_SHORT[item.genre] || item.voice_label || "Пісня";
  const right = fmtDuration(item.duration) || fmtDate(item.created_at);
  const isMix = (item.genre_mix || []).length > 0;
  const title = item.song_title
    ? (item.song_title.length > 10 ? item.song_title.slice(0, 10).trimEnd() + "…" : item.song_title)
    : (isMix ? "Мікс" : (item.genre_label || item.description || "Пісня"));
  return <div className={"song-row" + (url ? "" : " pending")} data-testid={`song-${item.number || item.id}`}
    role="button" tabIndex={0}
    onClick={() => { if (url) onOpen(); else if (resume) onResume?.(); }}
    onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && (url || resume)) { e.preventDefault(); url ? onOpen() : onResume?.(); } }}>
    <span className={"song-cover" + (item.event ? " art" : "")}>
      <img src={cover} alt="" loading="lazy" />
    </span>
    <span className="song-main">
      <span className="song-title">{title}</span>
      <span className="song-sub">
        <span className="genre-badge">{badge}</span>
        {right && <span className="song-date">{right}</span>}
        {(!url || isDone(item)) && (
          <span className="song-state" data-testid="song-state"
            style={{ color: st.color, borderColor: st.border, background: st.bg }}>{st.label}</span>
        )}
      </span>
    </span>
    <Waveform seed={Number(item.number) || String(item.id || "").length || 1} />
    {resume ? (
      <div className="song-resume-row">
        <button className="song-resume" onClick={(e) => { e.stopPropagation(); onResume?.(); }}>
          Продовжити
        </button>
        <button className="song-info" aria-label="Деталі"
          onClick={(e) => { e.stopPropagation(); onDetails?.(); }}>
          <img src="/design/icons-lg/info.svg" alt="" />
        </button>
      </div>
    ) : (
      <>
        <button className="song-play" disabled={!url} aria-label="Прослухати"
          onClick={(e) => { e.stopPropagation(); if (url) onOpen(); }}>
          <img src="/design/icons-lg/music.svg" alt="" />
        </button>
        <button className="song-act song-dl" aria-label="Завантажити" disabled={!url}
          onClick={(e) => { e.stopPropagation(); if (url) onOpen(); }}><Download size={16} /></button>
        <button className="song-info" aria-label="Деталі"
          onClick={(e) => { e.stopPropagation(); onDetails?.(); }}>
          <img src="/design/icons-lg/info.svg" alt="" />
        </button>
      </>
    )}
  </div>;
}

function SongsSkeleton() {
  return <div className="songs-list" aria-hidden="true">
    {[0, 1, 2].map((i) => <div className="sk-row" key={i}>
      <span className="sk sk-cover" />
      <span className="sk sk-circle" />
      <span className="sk-lines"><span className="sk sk-line" /><span className="sk sk-line short" /></span>
      <span className="sk sk-wave" />
    </div>)}
  </div>;
}

function SongsEmpty({ onCreate }) {
  return <div className="empty-state">
    <span className="empty-icon"><Music2 size={26} /></span>
    <h3>У тебе ще немає пісень 🎵</h3>
    <p>Створи першу пісню для важливої людини або події.</p>
    <button className="hero-cta empty-cta" onClick={onCreate}><Music2 size={18} /> Створити пісню</button>
  </div>;
}

function pageNumbers(page, pages) {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set([1, pages, page, page - 1, page + 1]);
  const nums = [...set].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
}

function SongsList({ songs, loaded, error, limit, page = 1, pages = 1, onPage,
  onCreate, onRefresh, onResume }) {
  const [copied, setCopied] = useState("");
  const [details, setDetails] = useState(null);
  const [playerItem, setPlayerItem] = useState(null);
  if (!loaded && songs.length === 0) return <SongsSkeleton />;
  if (songs.length === 0) {
    if (error) return <div className="empty-state soft">
      <span className="empty-icon"><RefreshCw size={24} /></span>
      <h3>Не вдалося завантажити пісні</h3>
      <p>Спробуй ще раз — ми вже шукаємо, що пішло не так.</p>
      <button className="ghost-cta" onClick={onRefresh}><RefreshCw size={15} /> Спробувати ще раз</button>
    </div>;
    return <SongsEmpty onCreate={onCreate} />;
  }
  const list = limit ? songs.slice(0, limit) : songs;
  const open = (item) => {
    if (listenUrlOf(item)) setPlayerItem(item);
  };
  const copy = async (item) => {
    const url = listenUrlOf(item);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(item.id || item.number);
      setTimeout(() => setCopied(""), 1600);
    } catch {}
    setMenuKey(null);
  };
  return <div className="songs-list">
    {list.map((it) => {
      const key = it.id || it.number;
      return <SongRow key={key} item={it}
        onOpen={() => open(it)}
        onResume={() => onResume?.(it)}
        onDetails={() => setDetails(it)} />;
    })}
    {limit === 0 && pages > 1 && (
      <div className="songs-pager" data-testid="songs-pager">
        <button type="button" className="pg-btn" disabled={page <= 1}
          onClick={() => onPage?.(page - 1)} aria-label="Попередня">←</button>
        {pageNumbers(page, pages).map((n, i) => (
          n === "…"
            ? <span className="pg-dots" key={`d${i}`}>…</span>
            : <button type="button" key={n} data-testid={`songs-page-${n}`}
                className={"pg-btn" + (n === page ? " active" : "")}
                onClick={() => onPage?.(n)}>{n}</button>
        ))}
        <button type="button" className="pg-btn" disabled={page >= pages}
          onClick={() => onPage?.(page + 1)} aria-label="Наступна">→</button>
      </div>
    )}
    {copied && <span className="copy-toast"><Check size={13} /> Посилання скопійовано</span>}
    <SongDetailsModal item={details} onClose={() => setDetails(null)} />
    <SongTracksModal item={playerItem} onClose={() => setPlayerItem(null)} />
  </div>;
}

function PackageCard({ p, onPick, active = false }) {
  const n = Math.max(1, Number(p.songs_count) || 1);
  const price = Math.round(Number(p.price_uah) || 0);
  const save = Math.max(0, Math.round((1 - price / (n * PRICE_PER_SONG)) * 100));
  return <button type="button" className={"pkg-card" + (p.is_popular ? " popular" : "") + (active ? " active" : "")}
    data-testid={`package-${p.key}`} onClick={() => onPick(p.key)}>
    <span className="pkg-price">{price} ₴</span>
    <span className="pkg-name">{p.emoji ? p.emoji + " " : ""}{p.name_ua}</span>
    <span className="pkg-meta">{n} {songWord(n)} · 2 варіанти на пісню</span>
    {save > 0 && <span className="pkg-save">-{save}%</span>}
    <span className="pkg-cta">{active ? <>Обрано <Check size={14} /></> : <>Обрати <ArrowRight size={14} /></>}</span>
    <span className="pkg-note"><i />{Math.round(price / n)} ₴ / пісня</span>
  </button>;
}

function Splash({ closing = false }) {
  return <div className={"ep-splash" + (closing ? " is-out" : "")} aria-hidden="true">
    <span className="ep-splash-wave"><i /><i /><i /><i /><i /><i /><i /></span>
    <span className="ep-splash-name">E-PISNYA</span>
  </div>;
}

function StubCard({ icon, title, text, actionLabel, href, onAction, loading }) {
  return <div className="stub-card">
    <span className="stub-icon">{icon}</span>
    <h3>{title}</h3>
    <p>{text}</p>
    {loading
      ? <span className="stub-loading"><Loader2 size={15} className="spin" /> Завантажую…</span>
      : href
        ? <a className="ghost-cta" href={href} target="_blank" rel="noreferrer">{actionLabel} <ArrowRight size={15} /></a>
        : <button className="ghost-cta" onClick={onAction}>{actionLabel} <ArrowRight size={15} /></button>}
  </div>;
}

export default function AppHomePage() {
  const [section, setSection] = useState("home");
  const [wizard, setWizard] = useState(null);
  const [songs, setSongs] = useState([]);
  const [songsLoaded, setSongsLoaded] = useState(false);
  const [songsError, setSongsError] = useState(false);
  const [botLink, setBotLink] = useState("https://t.me/episnyabot");
  const [packages, setPackages] = useState([]);
  const [packagesLoaded, setPackagesLoaded] = useState(false);
  const [examples, setExamples] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifDismissed, setNotifDismissed] = useState(false);
  const [navIn, setNavIn] = useState(false);
  const [menuKey, setMenuKey] = useState(null);
  const [splash, setSplash] = useState("in");
  const [kbOpen, setKbOpen] = useState(false);
  const [bootReady, setBootReady] = useState(false);
  const [fsSettled, setFsSettled] = useState(false);
  const bootAt = useRef(Date.now());
  const [me, setMe] = useState(null);
  const [balOpen, setBalOpen] = useState(false);
  const contentRef = useRef(null);
  const isMobile = true; // всегда мобильный шелл — и на ПК (по требованию заказчика)
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const check = () => {
      api.get("/public/block-check", {
        params: { client_id: getClientId(), tg_id: getTgUser()?.id || "" },
      }).then((r) => { if (r.data?.blocked) setBlocked(true); }).catch(() => {});
    };
    check();
    const id = setInterval(check, 15000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    if (getTgUser()?.id) {
      const key = `episnya_enter_${getTgUser().id}`;
      let seen = false;
      try { seen = !!sessionStorage.getItem(key); } catch { /* ignore */ }
      if (!seen) {
        try { sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
        trackAppEnter();
      }
    }
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      const el = e.target?.closest?.('button, [role="button"], a');
      if (!el || el.disabled) return;
      const hf = window.Telegram?.WebApp?.HapticFeedback;
      if (!hf) return;
      try {
        if (el.matches?.(".oc, .epw-card, .epw-chip, .epw-vchip")) hf.selectionChanged?.();
        else hf.impactOccurred?.("light");
      } catch {}
    };
    window.addEventListener("pointerdown", onDown, { passive: true, capture: true });
    return () => window.removeEventListener("pointerdown", onDown, { capture: true });
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const baseH = window.innerHeight;
    const baseVV = vv ? vv.height : baseH;
    const apply = () => {
      const vh = vv ? vv.height : window.innerHeight;
      const kbVV = Math.max(0, baseVV - vh);
      const layoutShrank = Math.max(0, baseH - window.innerHeight);
      const offset = Math.max(0, kbVV - layoutShrank);
      document.documentElement.style.setProperty("--kb-offset", Math.round(offset) + "px");
    };
    const isField = (el) => !!el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT");
    const onFocusIn = (e) => {
      if (!isField(e.target)) return;
      apply();
      document.body.classList.add("kb-open");
      setKbOpen(true);
    };
    const onFocusOut = () => {
      setTimeout(() => {
        if (isField(document.activeElement)) return;
        document.body.classList.remove("kb-open");
        document.documentElement.style.setProperty("--kb-offset", "0px");
        setKbOpen(false);
      }, 80);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    if (vv) {
      vv.addEventListener("resize", apply);
      vv.addEventListener("scroll", apply);
    }
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      if (vv) {
        vv.removeEventListener("resize", apply);
        vv.removeEventListener("scroll", apply);
      }
      document.body.classList.remove("kb-open");
      document.documentElement.style.setProperty("--kb-offset", "0px");
      setKbOpen(false);
    };
  }, []);

  useEffect(() => {
    const wa = window.Telegram?.WebApp;
    try {
      wa?.ready?.(); wa?.expand?.();
      wa?.setHeaderColor?.("#030B18");
      wa?.setBackgroundColor?.("#030B18");
    } catch {}
  }, []);

  useEffect(() => {
    const wa = window.Telegram?.WebApp;
    if (!wa || typeof wa.requestFullscreen !== "function") return;
    let unsupported = false;
    const tryFs = () => {
      if (unsupported || wa.isFullscreen) return;
      try { wa.requestFullscreen(); } catch { unsupported = true; }
    };
    const onFail = (e) => { if (e?.error === "UNSUPPORTED") unsupported = true; };
    const onFs = () => {
      try {
        if (wa.isFullscreen) wa.disableVerticalSwipes?.();
        else wa.enableVerticalSwipes?.();
      } catch {}
    };
    try { wa.onEvent?.("fullscreenFailed", onFail); wa.onEvent?.("fullscreenChanged", onFs); } catch {}
    if (wa.isFullscreen) onFs();
    const t = setTimeout(tryFs, 0);
    const onTap = () => tryFs();
    window.addEventListener("pointerdown", onTap, { once: true, passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("pointerdown", onTap);
    };
  }, []);

  useEffect(() => {
    const urls = [A + (isMobile ? "banners/blue-mobile.webp" : "banners/blue-desktop.webp"), ...HOME_EVENTS.slice(0, 4).map((k) => A + "events3d/" + k + ".webp")];
    let done = 0;
    const mark = () => { done += 1; if (done >= urls.length) setBootReady(true); };
    urls.forEach((u) => {
      const im = new Image();
      im.decoding = "async";
      im.onload = mark;
      im.onerror = mark;
      im.src = u;
    });
    const t = setTimeout(() => setBootReady(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--splash-cy", Math.round(window.innerHeight / 2) + "px");
  }, []);

  useEffect(() => {
    const wa = window.Telegram?.WebApp;
    let t;
    const schedule = (ms) => { clearTimeout(t); t = setTimeout(() => setFsSettled(true), ms); };
    const check = () => {
      let stable = true;
      try {
        stable = !wa || typeof wa.viewportStableHeight !== "number"
          || wa.viewportHeight === wa.viewportStableHeight;
      } catch { stable = false; }
      schedule(stable ? 250 : 700);
    };
    try { wa?.onEvent?.("viewportChanged", check); wa?.onEvent?.("fullscreenChanged", check); } catch {}
    check();
    const max = setTimeout(() => setFsSettled(true), 3000);
    return () => { clearTimeout(t); clearTimeout(max); };
  }, []);

  useEffect(() => {
    if (splash === "in") return undefined;
    const id = setTimeout(() => setNavIn(true), 350);
    return () => clearTimeout(id);
  }, [splash]);

  useEffect(() => {
    if (!bootReady || !fsSettled || splash !== "in") return;
    const wait = Math.max(0, 800 - (Date.now() - bootAt.current));
    const id = setTimeout(() => setSplash("out"), wait);
    return () => clearTimeout(id);
  }, [bootReady, fsSettled, splash]);

  useEffect(() => {
    api.get("/public/stats").then((r) => {
      if (r.data?.bot_link) setBotLink(r.data.bot_link);
    }).catch(() => {});
    api.get("/public/packages").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setPackages(list.filter((p) => p && p.key));
      setPackagesLoaded(true);
    }).catch(() => setPackagesLoaded(true));
  }, []);

  useEffect(() => {
    api.get("/public/app/examples", { params: { t: Date.now() } })
      .then((r) => setExamples((r.data?.items || []).filter((it) => it && it.ex_id && it.url)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tgId = getTgUser()?.id;
    if (!tgId) return;
    api.get("/public/app/me", { params: { tg_id: tgId } })
      .then((r) => setMe(r.data || null))
      .catch(() => {});
  }, []);

  const pageRef = useRef(1);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [songsTab, setSongsTab] = useState("songs");
  const [videoInfo, setVideoInfo] = useState(null);
  const [videoSend, setVideoSend] = useState("");
  const [videoLeft, setVideoLeft] = useState(0);
  const infoVideoRef = useRef(null);
  const openVideoFullscreen = () => {
    const v = infoVideoRef.current;
    if (!v) return;
    try { v.play().catch(() => {}); } catch { /* noop */ }
    try {
      if (typeof v.webkitEnterFullscreen === "function") v.webkitEnterFullscreen();
      else if (typeof v.requestFullscreen === "function") v.requestFullscreen().catch(() => {});
      else v.setAttribute("controls", "controls");
    } catch { v.setAttribute("controls", "controls"); }
  };
  const openVideoInfo = (v) => {
    setVideoSend("");
    const sa = v?.sent_at ? Date.parse(v.sent_at) : 0;
    setVideoLeft(sa ? Math.max(0, 600 - Math.floor((Date.now() - sa) / 1000)) : 0);
    setVideoInfo(v);
  };
  useEffect(() => {
    if (videoLeft <= 0) return undefined;
    const t = setInterval(() => setVideoLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [videoLeft > 0]);  // eslint-disable-line react-hooks/exhaustive-deps
  const sendVideoToBot = async (v) => {
    setVideoSend("busy");
    try {
      await api.post(`/public/app/video/${v.id}/send-to-bot`, {});
      setVideoSend("sent");
      setVideoLeft(600);
    } catch (e) {
      const d = String(e?.response?.data?.detail || "");
      const m = d.match(/rate-limit:(\d+)/);
      if (m) { setVideoLeft(Number(m[1])); setVideoSend(""); }
      else setVideoSend("fail");
    }
  };
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const loadVideos = useCallback(() => {
    setVideosLoading(true);
    api.get("/public/app/videos")
      .then((r) => setVideos(r.data?.items || []))
      .catch(() => setVideos([]))
      .finally(() => setVideosLoading(false));
  }, []);
  const loadSongs = useCallback(() => {
    const cid = getClientId();
    const tgId = getTgUser()?.id;
    if (!cid && !tgId) { setSongsLoaded(true); return; }
    setSongsError(false);
    const params = { done_first: 1, page: pageRef.current, per_page: 10 };
    if (cid) params.client_id = cid;
    if (tgId) params.tg_id = tgId;
    api.get("/public/landing/state", { params })
      .then((r) => {
        setSongs(sortSongs(r.data?.items || []));
        setPages(Math.max(1, Number(r.data?.pages) || 1));
        setPage(Math.max(1, Number(r.data?.page) || 1));
        setSongsLoaded(true);
      })
      .catch(() => { setSongsError(true); setSongsLoaded(true); });
  }, []);
  const goPage = useCallback((pg) => {
    pageRef.current = Math.max(1, pg);
    setPage(pageRef.current);
    loadSongs();
  }, [loadSongs]);

  useEffect(() => {
    loadSongs();
    const id = setInterval(loadSongs, 20000);
    const onVis = () => { if (document.visibilityState === "visible") loadSongs(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadSongs]);

  // SSE: мгновенные обновления статусов заказов (карточка обновляется на месте)
  useEffect(() => {
    const cid = getClientId();
    const tgId = getTgUser()?.id;
    if (!cid && !tgId) return undefined;
    const qs = new URLSearchParams();
    if (cid) qs.set("client_id", cid);
    if (tgId) {
      qs.set("tg_id", String(tgId));
      const idata = window.Telegram?.WebApp?.initData || "";
      if (idata) qs.set("init_data", idata);
    }
    let es;
    try { es = new EventSource(`${API}/public/landing/stream?${qs.toString()}`); } catch { return undefined; }
    es.onmessage = (e) => {
      let ev;
      try { ev = JSON.parse(e.data); } catch { return; }
      if (ev?.block) { setBlocked(true); return; }
      if (!ev?.id) return;
      setSongs((prev) => {
        const i = prev.findIndex((s) => String(s.id) === String(ev.id));
        if (i < 0) { loadSongs(); return prev; }
        const next = [...prev];
        next[i] = { ...next[i], ...ev };
        return sortSongs(next);
      });
    };
    return () => { try { es.close(); } catch {} };
  }, [loadSongs]);

  useEffect(() => {
    setMenuKey(null);
    contentRef.current?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [section, wizard]);

  useEffect(() => {
    const bb = window.Telegram?.WebApp?.BackButton;
    if (!bb) return undefined;
    const onBack = () => goSection("home");
    if (section === "examples" || section === "faq") {
      try { bb.onClick(onBack); bb.show(); } catch {}
    } else {
      try { bb.hide(); } catch {}
    }
    return () => { try { bb.offClick(onBack); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const openCreate = (event = "", packageKey = "", draft = null) => {
    trackApp("клік: створити пісню", event || "з головної");
    setWizard({ event, packageKey, draft });
  };

  const openTopup = (packageKey = "") => {
    trackApp("клік: поповнити баланс", packageKey || "з тарифів");
    setWizard({ topup: true, packageKey });
  };
  const closeWizard = () => { setWizard(null); loadSongs(); };
  const goSection = (id) => {
    const label = id === "faq"
      ? "faq: відкрив розділ"
      : (id === "examples" ? "приклади: відкрив розділ" : `перехід: ${id}`);
    trackApp(label, "");
    setBellOpen(false); setBalOpen(false); setWizard(null); setSection(id);
    if (id === "songs") loadSongs();
  };

  const songsBlock = (limit) => <SongsList songs={songs} loaded={songsLoaded} error={songsError}
    limit={limit} page={page} pages={pages} onPage={goPage}
    onCreate={() => openCreate("")} onRefresh={loadSongs}
    onResume={(item) => setWizard({ draft: item })}
    menuKey={menuKey} setMenuKey={setMenuKey} />;
  const draftItem = songs.find((s) => s.status === "unpaid") || null;
  const hasOrders = songs.length > 0;

  const homeNode = (mobile) => <div>
    <Hero mobile={mobile} onCta={() => openCreate("")} />
    <section>
      <SectionTitle action="Всі шаблони" onAction={() => goSection("templates")}>Популярні події</SectionTitle>
      <EventCards onPick={openCreate} grid={mobile} />
    </section>
    <section>
      <SectionTitle>Відео з піснею</SectionTitle>
      <button type="button" className="vid-home-card" data-testid="video-entry"
        onClick={() => goSection("video")}>
        <span className="vid-home-visual">
          <img src="/design/video-hero.webp" alt="" />
        </span>
        <span className="vid-home-text">
          <b>Музика на фото</b>
          <i>Відео з вашими фото під вашу пісню</i>
        </span>
        <ChevronRight size={20} className="vid-home-arrow" />
      </button>
    </section>
    {examples.length > 0 && <ExamplesBanner onOpen={() => goSection("examples")} />}
    <section>
      <SectionTitle action="Всі пісні" onAction={() => goSection("songs")}>Мої пісні</SectionTitle>
      {songsBlock(mobile ? 3 : 4)}
    </section>
  </div>;

  const sectionNode = (() => {
    switch (section) {
      case "songs":
        return <div>
          <div className="sec-head">
            <h2>Мої пісні</h2>
            <button className="sec-link"
              onClick={() => (songsTab === "video" ? loadVideos() : loadSongs())}>
              <RefreshCw size={13} /> Оновити</button>
          </div>
          <div className="songs-switch" role="tablist">
            <button type="button" role="tab" data-testid="tab-songs"
              className={songsTab === "songs" ? "is-on" : ""}
              onClick={() => setSongsTab("songs")}>Пісні</button>
            <button type="button" role="tab" data-testid="tab-videos"
              className={songsTab === "video" ? "is-on" : ""}
              onClick={() => { setSongsTab("video"); loadVideos(); }}>Відео</button>
          </div>
          {songsTab === "songs"
            ? songsBlock(0)
            : <VideosList items={videos} loading={videosLoading} onInfo={openVideoInfo} />}
        </div>;
      case "templates":
        return <div>
          <div className="sec-head"><h2>Шаблони</h2></div>
          <p className="sec-sub">Обери привід — конструктор підставить його автоматично.</p>
          <EventCards onPick={openCreate} />
        </div>;
      case "pricing":
        return <div>
          <div className="sec-head"><h2>Тарифи</h2></div>
          <InfoGlassBadge className="pkg-info-badge">При покупці кількох пісень вони залишаються на балансі, поки ви їх не використаєте.</InfoGlassBadge>
          {!packagesLoaded && <div className="pkg-grid">
            {[0, 1, 2, 3, 4].map((i) => <div className="pkg-card skeleton" key={i}>
              <span className="sk sk-line" /><span className="sk sk-line short" />
            </div>)}
          </div>}
          {packagesLoaded && packages.length > 0 && <div className="pkg-grid">
            {packages.map((p) => <PackageCard key={p.key} p={p} onPick={(key) => openTopup(key)} />)}
            <PremiumCard />
          </div>}
          {packagesLoaded && packages.length === 0 && <StubCard
            icon={<BadgeIcon size={24} />}
            title="Пакети тимчасово недоступні"
            text="Спробуйте оновити сторінку трохи пізніше або напишіть у підтримку."
            actionLabel="Відкрити Telegram-бот" href={botLink} />}
        </div>;
      case "examples":
        return <div>
          <div className="sec-head"><h2>Приклади звучання</h2></div>
          <p className="sec-sub">Послухай, як звучать різні жанри пісень — і обери свій.</p>
          <ExamplesPage items={examples} onCreate={(draft) => openCreate("", "", draft || null)} />
        </div>;
      case "video":
        return <VideoCreate songs={songs} onSongsRefresh={loadSongs}
          onGoCreate={() => openCreate("")} trackApp={trackApp} />;
      case "faq":
        return <FaqPage onOpenExamples={() => goSection("examples")}
          onOpenPricing={() => goSection("pricing")} />;
      case "partner":
        return <div>
          <div className="sec-head"><h2>Партнерка</h2></div>
          <StubCard icon={<UsersRound size={24} />} title="Партнерська програма"
            text="Запрошуйте друзів та отримуйте бонуси за кожне замовлення. Реєстрація відкриється найближчим часом."
            actionLabel="Дізнатися першим" href={botLink} />
        </div>;
      case "settings":
        return <div>
          <div className="sec-head"><h2>Налаштування</h2></div>
          <StubCard icon={<Settings size={24} />} title="Профіль і підтримка"
            text="Мова інтерфейсу — українська. Зв'язок і підтримка — через Telegram-бот. Незабаром тут з'являться сповіщення та керування підпискою Преміум."
            actionLabel="Відкрити Telegram-бот" href={botLink} />
        </div>;
      default:
        return homeNode(false);
    }
  })();

  const wizardNode = wizard ? <AppCreateWizard initialEvent={wizard.event} initialPackage={wizard.packageKey}
    initialTopup={!!wizard.topup}
    initialDraft={wizard.draft || null} balance={me ? Number(me.balance) || 0 : 0}
    onBalanceUsed={() => { api.get("/public/app/me", { params: { tg_id: getTgUser()?.id } }).then((r) => setMe(r.data || null)).catch(() => {}); }}
    onClose={closeWizard} onOrdered={loadSongs}
    onGoSongs={() => { closeWizard(); goSection("songs"); }} /> : null;

  const initData = typeof window !== "undefined"
    ? (window.Telegram?.WebApp?.initData || "") : "";
  if (!initData) {
    return (
      <div className="fixed inset-0 bg-[#030B18] grid place-items-center px-6" data-testid="noauth-screen">
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="text-6xl select-none">🔒</div>
          <div className="text-sm text-[#9FB0C7] max-w-xs leading-relaxed">
            Застосунок відкривається тільки з Telegram. Запустіть бота — і він відкриє застосунок.
          </div>
          <a href={botLink} target="_blank" rel="noopener noreferrer"
            data-testid="noauth-bot"
            className="rounded-2xl px-6 py-3 font-bold text-sm text-white"
            style={{ background: "linear-gradient(135deg,#2BA8FF,#1E86E8)", boxShadow: "0 10px 30px rgba(43,168,255,0.35)" }}>
            Відкрити бота
          </a>
        </div>
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="fixed inset-0 bg-[#030B18] grid place-items-center" data-testid="blocked-screen">
        <div className="flex flex-col items-center gap-5">
          <div className="text-6xl select-none">🔒</div>
          <a href="https://t.me/episnyasupport" target="_blank" rel="noopener noreferrer"
            className="rounded-2xl px-6 py-3 font-bold text-sm text-white"
            style={{ background: "linear-gradient(135deg,#2BA8FF,#1E86E8)", boxShadow: "0 10px 30px rgba(43,168,255,0.35)" }}>
            💬 Написати в підтримку
          </a>
          <div className="text-sm text-[#9FB0C7]">Support: @episnyasupport</div>
        </div>
      </div>
    );
  }

  const navIdx = wizard ? 1 : MOBILE_NAV.findIndex((n) => n.id === section);

  return <div className="epshome">
    <Splash closing={splash === "out"} />
    {kbOpen && (
      <button type="button" className="kb-close" aria-label="Закрити клавіатуру"
        onClick={() => { try { document.activeElement?.blur?.(); } catch {} }}>
        <ChevronDown size={20} strokeWidth={2.4} />
      </button>
    )}
    <div className="desktop-app boot-in">
      <Sidebar section={section} onSection={goSection} onOpenCreate={openCreate}
        createActive={!!wizard} onPremium={() => window.open(botLink, "_blank", "noopener")} />
      <main className="main">
        {!wizard && <Topbar onFaq={() => goSection("faq")} faqActive={section === "faq"}
          bellOpen={bellOpen} onBell={() => setBellOpen((v) => !v)}
          onPlus={() => window.open(botLink, "_blank", "noopener")}
          me={me} balOpen={balOpen} onBal={() => { setBalOpen((v) => !v); setBellOpen(false); }}
          onTopup={() => { setBalOpen(false); goSection("pricing"); }} />}
        <div className={"content" + (wizard ? " wizard-open" : "")}>
          <div className="tab-swap" key={wizard ? "wizard" : section}>
            {wizard && !isMobile ? wizardNode : (!isMobile ? sectionNode : null)}
          </div>
        </div>
      </main>
    </div>

    <div className="mobile-app boot-in">
      {!wizard && <div className={"mobile-top-wrap" + ((bellOpen || balOpen) ? " pop-open" : "")}>
        <span className="mobile-top-glass" aria-hidden="true" />
        <header className="mobile-top glass-surface glass-surface--header">
          <Brand />
          <div className="mobile-actions">
            <BalanceChip me={me} open={balOpen} onToggle={() => { setBalOpen((v) => !v); setBellOpen(false); }} />
            <button className={"icon-btn icon-btn-sm notification-circle" + (bellOpen ? " active" : "")}
              onClick={() => { setBellOpen((v) => !v); setBalOpen(false); }} aria-label="Сповіщення">
              <img className="iconlg" src={A + "icons-lg/appointment-reminders.svg"} alt="" style={{ width: 18, height: 18 }} />
              {draftItem && !notifDismissed && <span className="notif-dot" aria-hidden="true" />}
            </button>
            <FaqTopButton active={section === "faq"} onClick={() => goSection("faq")} />
          </div>
        </header>
        <Notifications open={bellOpen} onClose={() => setBellOpen(false)}
          draft={draftItem && !notifDismissed ? draftItem : null}
          onDismiss={() => setNotifDismissed(true)}
          onResume={(d) => { setBellOpen(false); setWizard({ draft: d }); }} />
        <BalancePop open={balOpen} me={me} onClose={() => setBalOpen(false)}
          onTopup={() => { setBalOpen(false); goSection("pricing"); }} />
      </div>}
      <main className={"mobile-content" + (wizard ? " wizard-open" : "")} ref={contentRef}>
        <div className="tab-swap" key={wizard ? "wizard" : section}>
          {wizard && isMobile ? wizardNode : (isMobile ? (section === "home" ? homeNode(true) : sectionNode) : null)}
        </div>
      </main>
      {videoInfo && (
        <div className="epw-modal-wrap sd-wrap" role="dialog" aria-modal="true">
          <div className="epw-modal-back" onClick={() => setVideoInfo(null)} />
          <div className="epw-modal sd-modal" data-testid="video-info-modal">
            <div className="epw-modal-head">
              <div className="epw-modal-title">Відео</div>
              <button type="button" className="epw-iconbtn" onClick={() => setVideoInfo(null)}
                aria-label="Закрити"><X size={18} /></button>
            </div>
            <div className="sd-body">
              {videoInfo.url && (
                <div className="vid-info-frame">
                  <video ref={infoVideoRef} className="vid-info-player" playsInline
                    src={videoInfo.url} poster={videoInfo.thumb || undefined} />
                  <button type="button" className="vid-info-play" onClick={openVideoFullscreen}
                    aria-label="Відкрити на весь екран" data-testid="video-play-full" />
                </div>
              )}
              <div className="vid-info-row">
                <span className="vid-preview sm"><Music2 size={18} /></span>
                <span className="vid-song-info">
                  <b>{videoInfo.song_title || "Пісня"}</b>
                  <i>Музика у відео · {videoInfo.photos} фото · {videoInfo.format}</i>
                </span>
              </div>
              <button type="button" className="st-send" disabled={videoSend === "busy" || videoLeft > 0}
                onClick={() => sendVideoToBot(videoInfo)} data-testid="video-send-bot">
                {videoSend === "busy" ? <Loader2 size={16} className="spin" /> : null}
                {videoLeft > 0
                  ? `Можна через ${Math.floor(videoLeft / 60)}:${String(videoLeft % 60).padStart(2, "0")}`
                  : (videoSend === "sent" ? "Надіслано в бот"
                    : videoSend === "fail" ? "Не вдалося — спробуйте ще"
                    : "Надіслати в бот")}
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className={"bottom-nav glass-surface glass-surface--bottom-nav" + (navIn ? " nav-in" : "")}>
        <span className={"nav-cloud glass-surface glass-surface--active" + (navIdx < 0 ? " is-hidden" : "")}
          aria-hidden="true"
          style={navIdx >= 0 ? { transform: `translateX(${navIdx * 100}%)` } : undefined} />
        {MOBILE_NAV.map((n) => (
          <button key={n.id}
            className={(wizard ? n.id === "create" : section === n.id) ? "selected" : ""}
            onClick={() => (n.id === "create" ? openCreate("") : goSection(n.id))}>
            <span className="nav-ico"><IconLG name={n.icon} size={23} /></span><span>{n.label}</span>
            {n.id === "songs" && hasOrders && <span className="nav-dot" aria-hidden="true" />}
          </button>
        ))}
      </nav>
    </div>

  </div>;
}
